import { useCallback, useRef, useState } from "react";
import { addParamsToURL } from "@huub-web/utils";

import type { AnalysisPhoto } from "./PhotoSelectionModal";

type BackendPhotoListResponse = {
  data: {
    photos: Array<{
      id: string;
      source_url: string | null;
      room_type: string | null;
      photo_number: number | null;
    }>;
  };
};

type UsePhotosParams = {
  /**
   * API GET function from the consuming context (e.g. useTemplates().$get)
   */
  $get: <T = unknown>(endpoint: string) => Promise<T>;
  /**
   * walter_address_object_id for the property
   */
  walterId: string | undefined;
};

/**
 * Fetches interior photos from GET /interior-designer/photo on demand.
 * Caches results per walterId — does not refetch if already loaded
 * for the same property.
 */
export const usePhotos = ({
  $get,
  walterId,
}: UsePhotosParams): {
  photos: AnalysisPhoto[];
  loading: boolean;
  error: string | null;
  fetch: () => void;
} => {
  const [photos, setPhotos] = useState<AnalysisPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedForRef = useRef<string | null>(null);
  const fetchingRef = useRef(false);
  const $getRef = useRef($get);
  $getRef.current = $get;

  const fetch = useCallback(() => {
    if (!walterId) return;
    if (fetchingRef.current) return;
    if (loadedForRef.current === walterId) return;

    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    setPhotos([]);

    $getRef
      .current<BackendPhotoListResponse>(
        addParamsToURL("/interior-designer/photo", {
          walter_address_object_id: walterId,
        }),
      )
      .then((response) => {
        const mapped = response.data.photos.map((p) => ({
          id: p.id,
          sourceUrl: p.source_url,
          roomType: p.room_type,
          photoNumber: p.photo_number,
        }));
        setPhotos(mapped);
        loadedForRef.current = walterId;
      })
      .catch((err: unknown) => {
        const detail = (err as { response?: { data?: { detail?: string } } })
          ?.response?.data?.detail;
        setError(detail ?? "PHOTO_FETCH_ERROR");
      })
      .finally(() => {
        fetchingRef.current = false;
        setLoading(false);
      });
  }, [walterId]);

  return { photos, loading, error, fetch };
};
